import React from 'react'
import { usePage } from '@inertiajs/react'
import GuideLayout from '@/components/help/GuideLayout'
import GuideSection from '@/components/help/GuideSection'
import GuideTip from '@/components/help/GuideTip'
import GuideRelatedLinks from '@/components/help/GuideRelatedLinks'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { FALLBACK_BRAND } from '@/lib/brand'
import type { SharedProps } from '@/types/shared'

export default function TargetsGuide({ currentUser, properties }: Pick<SharedProps, 'currentUser' | 'properties'>) {
  const brandProp = usePage().props.brand
  const brand = typeof brandProp === 'string' && brandProp.trim() !== '' ? brandProp : FALLBACK_BRAND

  return (
    <GuideLayout
      currentUser={currentUser}
      properties={properties}
      title="Targets"
      subtitle="Steer the rate engine toward your monthly occupancy, ADR, and RevPAR ambitions."
    >
      {/* ── 1. What is target setting ─────────────────────────────────────── */}
      <GuideSection title="What is target setting?">
        <p className="text-sm leading-relaxed text-muted-foreground" id="what-is-target-setting">
          A <strong className="text-foreground">target</strong> is a monthly commitment you make —
          for example, 75% occupancy in May. Once saved, the rate engine computes a
          {' '}<strong className="text-foreground">projected end-of-month</strong> occupancy every
          cycle by combining on-the-books bookings with normal remaining pickup. It then compares
          that projection against your target and adjusts upcoming rates to help close any gap.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Targets are <strong className="text-foreground">opt-in and reversible</strong>. Properties
          without a saved target are completely unaffected — the engine continues pricing from
          historical baselines exactly as before. Saving a target is the switch that turns the
          feature on. Targets work for any future month — not just the current one.
        </p>
      </GuideSection>

      {/* ── 2. How the engine uses targets ────────────────────────────────── */}
      <GuideSection title="How does the engine use my targets?" id="how-engine-uses-targets">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Every computation cycle the engine computes a{' '}
          <strong className="text-foreground">target gap</strong>:
        </p>
        <div className="rounded-lg border border-border/60 bg-muted/30 px-4 py-3 text-sm font-mono tabular-nums text-foreground">
          target gap = projected end-of-month − target (in occupancy points — &ldquo;pts&rdquo;)
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          The engine then responds based on the gap and a{' '}
          <strong className="text-foreground">noise floor</strong> — a horizon-adjusted deadband
          that prevents small projection fluctuations from churning rates:
        </p>
        <div className="overflow-hidden rounded-xl border border-border/60">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className="text-[11px] uppercase tracking-wider">Target gap</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider">What happens</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium text-foreground">Within the noise floor</TableCell>
                <TableCell className="text-muted-foreground">On track — projection is close enough to target. Rates unchanged by the target stage.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium text-rose-600">Below target (beyond noise floor)</TableCell>
                <TableCell className="text-muted-foreground">Pushing — the engine applies a downward modifier to upcoming dates to stimulate bookings.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium text-emerald-600">Above target (beyond noise floor)</TableCell>
                <TableCell className="text-muted-foreground">Easing — the engine firms rates to capture more revenue per booking.</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          <strong className="text-foreground">Example:</strong> You set May's occupancy target to
          75%. On 15 May the engine forecasts 58% projected end-of-month — 17 pts below target.
          That's well outside the noise floor, so the engine is pushing harder on remaining May
          dates. You'll see a{' '}
          <span className="inline-flex items-center rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700">Pushing</span>{' '}
          badge on the dashboard Target Outlook card.
        </p>
      </GuideSection>

      {/* ── 2b. Reading the forecast: band, velocity, day types ──────────────── */}
      <GuideSection title="Reading the forecast: band, velocity, and day types" id="reading-the-forecast">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The forecast is more than a single projected number. Three signals help you read how
          confident and how timely it is — all shown on the Target Detail page.
        </p>

        <h4 className="mt-4 text-sm font-semibold text-foreground" id="forecast-band">Forecast band</h4>
        <p className="text-sm leading-relaxed text-muted-foreground">
          The <strong className="text-foreground">forecast band</strong> is the plausible range
          around the projected end-of-month, drawn from your property&rsquo;s own pickup
          variability (the P10&ndash;P90 range). It is shaded around the projected line on the
          trajectory chart. A <strong className="text-foreground">narrow band</strong> means
          pickup at this horizon has been predictable; a{' '}
          <strong className="text-foreground">wide band</strong> means it has been hard to call.
          The band also sets the <strong className="text-foreground">noise floor</strong> — the
          deadband below which the target gap is treated as noise and the engine takes no action.
          When a band has been measured, the noise floor is{' '}
          <em>band-based</em>; before then it falls back to a horizon estimate.
        </p>

        <h4 className="mt-4 text-sm font-semibold text-foreground" id="booking-velocity">Booking velocity</h4>
        <p className="text-sm leading-relaxed text-muted-foreground">
          <strong className="text-foreground">Booking velocity</strong> compares this
          month&rsquo;s recent observed pickup against the curve-expected pickup over the trailing
          window, and words it on the same scale as booking pace:{' '}
          <strong className="text-foreground">on track</strong> means it is booking on tempo;{' '}
          <strong className="text-emerald-600">ahead of the curve</strong> is faster than usual;{' '}
          <strong className="text-rose-600">behind the curve</strong> is slower than usual. The
          projected end-of-month already reflects this tempo, so velocity is the early
          warning — it moves weeks before the gap would otherwise show up. Far from month-end the
          trailing window has only seen a small share of the month&rsquo;s remaining pickup, so
          velocity is weighted down and may read{' '}
          <em>&ldquo;mostly silent at this horizon&rdquo;</em> — that is expected, not a fault.
        </p>

        <h4 className="mt-4 text-sm font-semibold text-foreground" id="weekday-weekend">Weekday vs weekend</h4>
        <p className="text-sm leading-relaxed text-muted-foreground">
          You set one occupancy target for the month. Behind the scenes the engine decomposes it
          into implied <strong className="text-foreground">weekday</strong> and{' '}
          <strong className="text-foreground">weekend</strong> sub-targets using your
          property&rsquo;s own historical day-type mix, then measures a{' '}
          <strong className="text-foreground">weekday gap</strong> and{' '}
          <strong className="text-foreground">weekend gap</strong> separately. This sends the
          pressure where the shortfall actually lives: a near-full Saturday is not discounted to
          chase a midweek gap. The Weekday vs weekend table on the Target Detail page shows the
          projected end-of-month, the implied target, and the gap for each day type; the calendar
          day card shows the gap for that specific date&rsquo;s day type.
        </p>
        <GuideTip type="do">
          A wide forecast band, or a velocity reading of &ldquo;behind the curve,&rdquo; both mean
          &ldquo;watch, don&rsquo;t react yet.&rdquo; Give the engine a few cycles — the band narrows
          and the velocity firms up as more of the month&rsquo;s bookings arrive.
        </GuideTip>
      </GuideSection>

      {/* ── 2b. ADR floor & projections (C7, D14/D16) ─────────────────────── */}
      <GuideSection title="Protecting your rate: the ADR floor" id="adr-floor">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Chasing an occupancy target can tempt the engine into discounting too far — filling
          rooms by giving away rate. The <strong className="text-foreground">ADR floor</strong> is
          the safety line beneath Target Pressure: it is the rate the engine will{' '}
          <strong className="text-foreground">never discount below</strong> when steering toward
          your occupancy target. When chasing the target would push a lead rate under the floor,
          the engine <strong className="text-foreground">holds price at the floor instead</strong>{' '}
          — it would rather miss a little occupancy than quietly destroy your average daily rate.
        </p>

        <h4 className="mt-4 text-sm font-semibold text-foreground" id="adr-floor-where">Where the floor comes from</h4>
        <ul className="ml-4 mt-1 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">From your ADR target.</strong> If you save an{' '}
            <strong className="text-foreground">ADR</strong> target for the month alongside your
            occupancy target, that number <em>is</em> the floor. (An ADR target on its own does not
            switch on rate steering — only an occupancy target does that — but it sets the floor for
            when steering is active.)
          </li>
          <li>
            <strong className="text-foreground">A sensible default.</strong> If you set no ADR
            target, the engine still protects your margin: it floors at{' '}
            <strong className="text-foreground">85% of your own recent realized ADR</strong>,
            measured separately for weekdays and weekends over the trailing window. So a property
            whose recent weekday ADR was {'£'}79 gets a {'£'}67 weekday floor automatically
            — no configuration needed.
          </li>
        </ul>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          In a <strong className="text-foreground">combined inventory</strong>, the default is measured in the lead
          listing&apos;s prices. A night sold on a Same room that carries an offset counts as if it sold at its lead
          room&apos;s price, so the offset never moves the floor. Your reports still show what each night really sold
          for.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          The Target Detail page always shows the floor and where it came from — there are no silent
          defaults. Below the floor, your <strong className="text-foreground">minimum rate</strong>{' '}
          (set in Price Guardrails) remains the absolute hard limit; the ADR floor sits on top of it
          and only constrains target-driven discounts. Easing rates up is never touched by the floor.
        </p>

        <h4 className="mt-4 text-sm font-semibold text-foreground" id="adr-floor-binding">When the floor binds</h4>
        <p className="text-sm leading-relaxed text-muted-foreground">
          On the Affected dates table, any date where the floor held a discount back is marked{' '}
          <strong className="text-amber-700">ADR floor</strong>. If the floor binds on a large share
          of the dates the engine steered, you&rsquo;ll also see an alert:{' '}
          <em>&ldquo;your occupancy target wants discounts your ADR floor won&rsquo;t allow.&rdquo;</em>{' '}
          That is the honest trade-off, surfaced rather than silently resolved — the decision stays
          yours. To allow deeper discounts, <strong className="text-foreground">lower the ADR floor</strong>{' '}
          (or the occupancy target) for the month; to protect rate, leave it and accept the projected
          end-of-month as the realistic outcome.
        </p>

        <h4 className="mt-4 text-sm font-semibold text-foreground" id="projected-economics">
          Projected month economics
        </h4>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Beside the floor, the page shows your{' '}
          <strong className="text-foreground">projected month ADR</strong> and{' '}
          <strong className="text-foreground">projected RevPAR</strong> — where the month is heading
          on rate and revenue if current pickup continues at today&rsquo;s listed rates. These are{' '}
          <strong className="text-foreground">for context only</strong>: the engine does not steer on
          them. They build the picture that pairs with your occupancy projection so you can see the
          whole story — occupancy, rate, and the revenue they combine into.
        </p>
        <h4 className="mt-4 text-sm font-semibold text-foreground" id="building-adr">
          In a combined inventory: the building ADR
        </h4>
        <p className="text-sm leading-relaxed text-muted-foreground">
          When one building is sold through several listings, the target is the building&apos;s, so its
          ADR is the <strong className="text-foreground">building ADR</strong>: revenue from every
          listing&apos;s bookings divided by every listing&apos;s room-nights sold. The default floor is 85%
          of the recent building ADR, with any Same room offset taken out as described above, and the
          month&apos;s results and projection read the building ADR as sold. When a following listing sells
          some of the lead listing&apos;s rooms as Same rooms, the floor is in the{' '}
          <strong className="text-foreground">lead listing&apos;s prices</strong>: it holds the price the engine
          sets, and those Same rooms sell from the lead listing&apos;s price, plus any offset of their own. The
          Targets pages say so beside the floor.
        </p>
        <GuideTip type="do">
          Think of the floor as a margin guardrail, not a target. Leaving the 85% default in place is
          a safe choice for most months; set an explicit ADR target only when you have a specific
          rate you want to defend.
        </GuideTip>
      </GuideSection>

      {/* ── 2c. Dilution guard (C8, D15/D16) ──────────────────────────────── */}
      <GuideSection title="Not discounting into demand that books anyway: the dilution guard" id="dilution-guard">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Some demand books no matter what you charge. A few days before arrival, the guests who are
          going to book have largely decided — a price cut there rarely wins extra rooms, it just{' '}
          <strong className="text-foreground">cheapens the rooms that would have sold anyway</strong>.
          The <strong className="text-foreground">dilution guard</strong> is the safety line for that
          situation: when chasing your occupancy target would discount into{' '}
          <strong className="text-foreground">price-inelastic, close-in demand</strong>, the engine{' '}
          <strong className="text-foreground">withholds the discount entirely</strong> rather than
          give away rate for occupancy it would have got at full price.
        </p>

        <h4 className="mt-4 text-sm font-semibold text-foreground" id="dilution-guard-how">How it decides</h4>
        <p className="text-sm leading-relaxed text-muted-foreground">
          The engine carries a per-property view of how price-sensitive demand is at each lead time —
          inelastic close to arrival, more elastic weeks out (it is cheaper to win a booking with a
          discount when there is still time for guests to shop around). The guard holds a discount
          back only when <strong className="text-foreground">all three</strong> are true:
        </p>
        <ul className="ml-4 mt-1 list-disc space-y-2 text-sm text-muted-foreground">
          <li>the engine is trying to <strong className="text-foreground">discount</strong> the date (chasing the target down);</li>
          <li>the date is <strong className="text-foreground">close in</strong>, where demand is price-inelastic (a cut buys little extra occupancy); and</li>
          <li>the date is <strong className="text-foreground">not in distress</strong> — it is already projected to fill at a healthy level. If projected occupancy is very low, the guard stands aside: an empty room earns nothing, so filling it at a discount is the right call.</li>
        </ul>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          The guard <strong className="text-foreground">only ever withholds</strong> a discount — it
          never lowers a rate, and it never touches a rate that is easing upward. So if the underlying
          view of elasticity is imperfect, the worst it can do is leave the engine a little
          conservative; it can never make it reckless. The elasticity view is a starting prior, tunable
          per property, that the system will refine over time from your own results.
        </p>

        <h4 className="mt-4 text-sm font-semibold text-foreground" id="dilution-guard-binding">When the guard binds</h4>
        <p className="text-sm leading-relaxed text-muted-foreground">
          On the Affected dates table, a date whose discount was withheld is marked{' '}
          <strong className="text-sky-700">Dilution guard</strong> with the explanation{' '}
          <em>&ldquo;at N days out, demand here books regardless of price; cutting would only cheapen
          rooms that would have sold.&rdquo;</em> The Target Detail page also shows what share of
          steered dates the guard withheld. If the guard <em>and</em> the ADR floor together hold price
          on most of the dates the engine tried to steer while you are still behind target, you&rsquo;ll
          see a warning: <em>&ldquo;target unreachable without breaching your guardrails.&rdquo;</em>{' '}
          That is the honest trade-off, surfaced rather than silently resolved — to close the gap you
          can <strong className="text-foreground">lower the ADR floor</strong>,{' '}
          <strong className="text-foreground">lower the occupancy target</strong> for the month, or{' '}
          accept the projected end-of-month as the realistic outcome. The choice stays yours.
        </p>
        <GuideTip type="do">
          A high <strong>Dilution guard</strong> share is usually the engine protecting you, not a
          problem: it means your close-in demand is strong and does not need discounting. Worry about
          it only when you are genuinely behind target and want to trade rate for occupancy — in which
          case the guardrails are telling you that trade is no longer cheap.
        </GuideTip>
      </GuideSection>

      {/* ── 2d. Forecast accuracy / calibration (C9, D17) ─────────────────── */}
      <GuideSection title="How honest the forecast has been: forecast accuracy" id="forecast-accuracy">
        <p className="text-sm leading-relaxed text-muted-foreground">
          A forecast is only as useful as it is honest. As each month closes, the engine compares
          what it <strong className="text-foreground">projected</strong> at every horizon against
          what <strong className="text-foreground">actually happened</strong>, and keeps a running
          record — the <strong className="text-foreground">accuracy ledger</strong>. The Target
          Detail page surfaces it as <strong className="text-foreground">Forecast accuracy</strong>:
          a table of how far off the projections have run at each horizon (0&ndash;7, 8&ndash;21,
          22&ndash;45, and 46&ndash;90 days before month-end).
        </p>

        <h4 className="mt-4 text-sm font-semibold text-foreground" id="forecast-accuracy-error">Reading the table</h4>
        <ul className="ml-4 mt-1 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Avg error</strong> is the average signed gap between
            the projection and the realized end-of-month occupancy.{' '}
            <strong className="text-rose-600">Positive</strong> means the engine has been{' '}
            <em>over</em>-projecting (occupancy came in lower than forecast);{' '}
            <strong className="text-emerald-600">negative</strong> means it has been{' '}
            <em>under</em>-projecting.
          </li>
          <li>
            <strong className="text-foreground">Months</strong> is how many closed months have been
            measured at that horizon — the weight of evidence behind the figure.
          </li>
          <li>
            <strong className="text-foreground">Typical miss</strong> is the average size of the
            error regardless of direction — a feel for how tight the forecast has been.
          </li>
        </ul>

        <h4 className="mt-4 text-sm font-semibold text-foreground" id="forecast-calibration">Forecast calibration</h4>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Where the projections have <em>consistently</em> run high or low at a horizon, the engine
          applies a <strong className="text-foreground">forecast calibration</strong> — it nudges the
          next projection back toward where reality has landed. The calibration is{' '}
          <strong className="text-foreground">deliberately cautious</strong>:
        </p>
        <ul className="ml-4 mt-1 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            after <strong className="text-foreground">one</strong> closed month it applies only a
            small share of the measured error (around a seventh of it);
          </li>
          <li>
            it strengthens as months accumulate — about a third after three months — approaching
            full strength only once there is real evidence; and
          </li>
          <li>
            it is <strong className="text-foreground">capped</strong>, so a single freak month can
            never swing the forecast wildly.
          </li>
        </ul>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          A property with <strong className="text-foreground">no closed months yet</strong> gets no
          calibration at all — its forecast is exactly the engine&rsquo;s uncalibrated projection. The
          banner at the top of the panel tells you what calibration, if any, is being applied to{' '}
          <em>this</em> month right now, at its current distance from close.
        </p>
        <GuideTip type="do">
          Read the accuracy ledger as a credibility statement, not a to-do. If it shows a steady bias
          at the horizon you care about, that bias is already being corrected; you don&rsquo;t need to
          adjust anything. If the typical miss is large and erratic, treat the forecast as
          directional and lean on the forecast band and velocity as well.
        </GuideTip>
      </GuideSection>

      {/* ── 3. Setting a target ───────────────────────────────────────────── */}
      <GuideSection title="Setting a target" id="setting-a-target">
        <ol className="ml-4 list-decimal space-y-2 text-sm text-muted-foreground">
          <li>
            Open <strong className="text-foreground">Targets</strong> from the property
            sidebar (or top navigation).
          </li>
          <li>
            Find the month you want to configure — months are listed in calendar order.
          </li>
          <li>
            Enter the <strong className="text-foreground">Occupancy %</strong> (for example, 75),{' '}
            <strong className="text-foreground">ADR</strong> (for example, 85.00), or{' '}
            <strong className="text-foreground">RevPAR</strong> (for example, 63.75). You can set
            any combination — occupancy alone is the most common starting point and is required for
            the forecast-steering engine to activate.
          </li>
          <li>
            The <strong className="text-foreground">impact preview panel</strong> on the right
            shows a live estimate of how the targets will shift upcoming rates, based on the
            forecast-vs-target gap for each drafted month.
          </li>
          <li>
            When you're ready, click{' '}
            <strong className="text-foreground">Save &amp; deploy</strong>. A confirmation dialog
            shows the expected impact. Confirm, and the targets are saved immediately.
          </li>
          <li>
            After saving, two things happen in sequence:{' '}
            <strong className="text-foreground">the forecast refreshes in ~5 seconds</strong>{' '}
            (a new projected end-of-month is computed), then{' '}
            <strong className="text-foreground">rates update within ~60 seconds</strong>{' '}
            (the next computation cycle applies the new target gap to upcoming dates).
          </li>
        </ol>
        <GuideTip type="do">
          Use last year's actuals (shown beneath each input) as a starting point. {brand} flags
          targets that diverge more than 25 pts from last year so you can verify the number is
          intentional.
        </GuideTip>
      </GuideSection>

      {/* ── 4. Reading the dashboard card ────────────────────────────────── */}
      <GuideSection title="Reading the Target Outlook card" id="reading-the-dashboard-card">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The <strong className="text-foreground">Target Outlook card</strong> appears on your
          dashboard for every month that has a saved target. Here's what each element means:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">State badge</strong> — one of five states
            (see the table below). Updates every computation cycle (~2 hrs) and immediately
            after you save a target.
          </li>
          <li>
            <strong className="text-foreground">Narration</strong> — a one-sentence plain-language
            summary of what the engine is doing, e.g.{' '}
            <em>"Projected end-of-month 61.2% vs target 75% — pushing harder on remaining dates (16 days)"</em>.
          </li>
          <li>
            <strong className="text-foreground">Sparkline</strong> — tracks how the projected
            end-of-month has moved over recent snapshots. A rising line means confidence is
            building; a flat or falling line may warrant action.
          </li>
          <li>
            <strong className="text-foreground">Projected end-of-month</strong> — the engine's
            forecast of where you'll finish the month if current pickup continues. If it's above
            your target, the engine is easing. If it's below, the engine is pushing.
          </li>
          <li>
            <strong className="text-foreground">View details</strong> — opens the Target Detail
            page with a full forecast chart, engine-actions timeline, and rate-level breakdown.
          </li>
        </ul>
      </GuideSection>

      {/* ── 5. The five states ────────────────────────────────────────────── */}
      <GuideSection title="The five states" id="the-five-states">
        <div className="overflow-hidden rounded-xl border border-border/60">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className="text-[11px] uppercase tracking-wider">State</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider">What it means</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider hidden sm:table-cell">What you might do</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>
                  <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700">On track</span>
                </TableCell>
                <TableCell className="text-muted-foreground">Projected end-of-month is within the noise floor of your target. No target-stage adjustment applied.</TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">Nothing. Check back tomorrow.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <span className="inline-flex items-center rounded-full bg-rose-100 px-2.5 py-1 text-[11px] font-semibold text-rose-700">Pushing</span>
                </TableCell>
                <TableCell className="text-muted-foreground">Projected end-of-month is below target (beyond noise floor). Engine is lowering rates on upcoming dates to stimulate bookings.</TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">Monitor for 5 days. If the projected end-of-month doesn't rise, consider lowering the target.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">Easing</span>
                </TableCell>
                <TableCell className="text-muted-foreground">Projected end-of-month is above target (beyond noise floor). Engine is firming rates to capture more revenue per booking.</TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">Consider raising next month's target to capture more revenue.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-700">At floor</span>
                </TableCell>
                <TableCell className="text-muted-foreground">Engine wants to lower rates further, but many dates in this month are already at the minimum rate. Cannot push further.</TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">Lower the occupancy target, or lower the floor in Price Guardrails.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">Dormant</span>
                </TableCell>
                <TableCell className="text-muted-foreground">A target is saved but Target Pressure is not adjusting rates because the forecast is not yet actionable. See dormant reasons below.</TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">Usually resolves on its own. See the FAQ section below.</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          An additional <strong className="text-foreground">Demand cap trim</strong> indicator may
          appear alongside any active state. This means the engine's intended adjustment was reduced
          because the combined effect of Target Pressure and Demand Response would have exceeded the
          demand cap. The underlying direction (pushing or easing) is still shown.
        </p>
      </GuideSection>

      {/* ── 6. Dormant states FAQ ────────────────────────────────────────── */}
      <GuideSection title="Why is my target dormant?" id="dormant-states">
        <p className="text-sm leading-relaxed text-muted-foreground">
          A target enters the <strong className="text-foreground">Dormant</strong> state when one of
          four conditions is met. The reason code is shown in the calendar day tooltip and on the
          Target Detail page.
        </p>
        <div className="overflow-hidden rounded-xl border border-border/60">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className="text-[11px] uppercase tracking-wider">Reason</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider">What it means</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider hidden sm:table-cell">What to expect</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium text-foreground text-xs">No pickup history</TableCell>
                <TableCell className="text-muted-foreground">The engine has no booking-curve baseline for this property yet. The forecast can't be trusted.</TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">Resolves automatically as booking-curve history accumulates (typically 1&ndash;2 months). No action needed.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium text-foreground text-xs">Industry default (preview)</TableCell>
                <TableCell className="text-muted-foreground">The engine is using a generic industry baseline instead of your property's own pickup history.</TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">The engine shows a preview of what it would do, but doesn't move rates yet. Will graduate to property history over time.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium text-foreground text-xs">Confidence below threshold</TableCell>
                <TableCell className="text-muted-foreground">The projection confidence score is below the required floor (default 30%). The forecast has too much uncertainty to act on.</TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">Usually improves as more bookings arrive closer to the month. May also improve as baseline samples grow.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium text-foreground text-xs">No pickup remaining</TableCell>
                <TableCell className="text-muted-foreground">The booking curve says no further bookings are expected — the month is effectively closed out.</TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">Normal for the last few days of a month. No action needed.</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </GuideSection>

      {/* ── 7. When to adjust ────────────────────────────────────────────── */}
      <GuideSection title="When to adjust" id="when-to-adjust">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Targets are a commitment, not a ceiling. The engine is designed to chase them without
          your intervention. But there are times when a manual adjustment makes sense:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Lower the target</strong> — when the engine has
            been pushing for more than a week and the projected end-of-month still doesn't close
            the gap, or when you're seeing the "At floor" state and the floor is appropriate.
          </li>
          <li>
            <strong className="text-foreground">Raise the target</strong> — when the "Easing"
            state has persisted for several days and the projected end-of-month is well above
            target. More ambitious targets can unlock more revenue.
          </li>
          <li>
            <strong className="text-foreground">Clear the target entirely</strong> — when you want
            the engine to return to pure historical-baseline pricing for that month. Use the Clear
            button on the target row, then save &amp; deploy.
          </li>
          <li>
            <strong className="text-foreground">Lower the floor</strong> (in Price Guardrails) —
            when you're seeing the "At floor" state and you want to give the engine more headroom
            to push harder.
          </li>
        </ul>
        <GuideTip type="dont">
          Don't adjust targets every day in reaction to short-term fluctuations. The forecast
          confidence mechanism smooths out noise — give the engine at least 5 days to respond
          before concluding the target isn't working.
        </GuideTip>
      </GuideSection>

      {/* ── 8. FAQ ────────────────────────────────────────────────────────── */}
      <GuideSection title="FAQ" id="faq">
        <div className="space-y-4 text-sm text-muted-foreground">
          <div>
            <p className="font-semibold text-foreground">What if I don't set a target?</p>
            <p className="mt-1 leading-relaxed">
              Nothing changes. The engine continues pricing from historical baselines exactly as
              before targets existed. There is no master switch — saving a target is the opt-in.
              Properties without a saved target will not see the Target Outlook card on the dashboard.
            </p>
          </div>
          <div>
            <p className="font-semibold text-foreground">Can I set targets for future months?</p>
            <p className="mt-1 leading-relaxed">
              Yes — targets work for any saved month, not just the current one. The engine
              evaluates each month's forecast independently. Setting a target for next month now
              lets the engine start steering as soon as bookings arrive.
            </p>
          </div>
          <div>
            <p className="font-semibold text-foreground">How quickly do saved targets take effect?</p>
            <p className="mt-1 leading-relaxed">
              Two-step: the forecast signal (projected end-of-month) refreshes in ~5 seconds after
              saving. The rates on upcoming dates update within ~60 seconds (next computation
              cycle). You'll see the Target Outlook card state change almost immediately; rate
              changes follow within a minute.
            </p>
          </div>
          <div>
            <p className="font-semibold text-foreground">Can I undo a deployed target?</p>
            <p className="mt-1 leading-relaxed">
              Yes. Go to Targets, find the month, click{' '}
              <strong className="text-foreground">Clear</strong> on the row, and then click{' '}
              <strong className="text-foreground">Save &amp; deploy</strong>. The engine returns to
              historical baselines on the next computation, within the hour.
            </p>
          </div>
          <div>
            <p className="font-semibold text-foreground">Does setting a target guarantee I'll hit it?</p>
            <p className="mt-1 leading-relaxed">
              No. A target steers the engine — it adjusts rates to make booking more or less
              attractive. But external factors (market demand, competition, seasonality) ultimately
              determine whether guests book. Targets work best when grounded in realistic
              demand expectations for the month.
            </p>
          </div>
          <div>
            <p className="font-semibold text-foreground">Why is there no "on/off" toggle for the whole feature?</p>
            <p className="mt-1 leading-relaxed">
              Saving a target is the toggle. Properties with no saved target are completely
              unaffected by the target stage — it produces a zero modifier. This keeps the
              pipeline deterministic: the same inputs always produce the same rates.
            </p>
          </div>
        </div>
      </GuideSection>

      <GuideRelatedLinks
        links={[
          { slug: 'dashboard', title: 'Dashboard' },
          { slug: 'demand-response', title: 'Demand Response' },
          { slug: 'price-guardrails', title: 'Price Guardrails' },
          { slug: 'how-it-works', title: 'How It Works' },
        ]}
      />
    </GuideLayout>
  )
}
